
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { RefreshCw, Eye, EyeOff } from "lucide-react"
import { useUpdateStaffMember, StaffMember, StaffType } from "@/hooks/useStaffMembers"
import { useCondominium } from "@/hooks/useCondominium"
import { formatCPF, validateCPF } from "@/utils/cpfValidation"
import { generatePassword } from "@/utils/passwordUtils"
import PhotoCapture from "./PhotoCapture"
import { useToast } from "@/hooks/use-toast"

interface EditarColaboradorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  colaborador: StaffMember
}

const EditarColaboradorModal = ({ open, onOpenChange, colaborador }: EditarColaboradorModalProps) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    staff_type: "" as StaffType,
    cpf: "",
    workload_hours: 8,
    photo_url: "",
    password: ""
  })
  const [showPassword, setShowPassword] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState("")
  const { toast } = useToast()

  const updateStaffMember = useUpdateStaffMember()
  const { data: condominium } = useCondominium()
  
  const isPontoEletronico = condominium?.type === 'ponto_eletronico'

  useEffect(() => {
    if (colaborador && open) {
      setFormData({
        full_name: colaborador.full_name,
        email: colaborador.email || "",
        phone: colaborador.phone || "",
        staff_type: colaborador.staff_type,
        cpf: colaborador.cpf || "",
        workload_hours: colaborador.workload_hours || 8,
        photo_url: colaborador.photo_url || "",
        password: ""
      })
      setGeneratedPassword("")
    }
  }, [colaborador, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.full_name || !formData.staff_type) {
      toast({
        title: "Campos obrigatórios",
        description: "Nome completo e função são obrigatórios.",
        variant: "destructive",
      })
      return
    }

    if (formData.cpf && !validateCPF(formData.cpf)) {
      toast({
        title: "CPF inválido",
        description: "Por favor, insira um CPF válido.",
        variant: "destructive",
      })
      return
    }

    try {
      await updateStaffMember.mutateAsync({
        id: colaborador.id,
        full_name: formData.full_name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        staff_type: formData.staff_type,
        cpf: formData.cpf || undefined,
        workload_hours: formData.workload_hours,
        photo_url: formData.photo_url || undefined,
        password: formData.password || undefined
      })
      
      onOpenChange(false)
    } catch (error: any) {
      console.error("Erro ao atualizar colaborador:", error)
      toast({
        title: "Erro ao atualizar",
        description: error?.message || "Erro desconhecido ao atualizar colaborador.",
        variant: "destructive",
      })
    }
  }

  const handleCpfChange = (value: string) => {
    const formatted = formatCPF(value)
    setFormData(prev => ({ ...prev, cpf: formatted }))
  }

  const getStaffTypeOptions = () => {
    if (isPontoEletronico) {
      return [
        { value: "gerente" as StaffType, label: "Gerente" },
        { value: "operador" as StaffType, label: "Operador" }
      ]
    }

    if (condominium?.type === 'controle_acessos') {
      return [
        { value: "porteiro" as StaffType, label: "Porteiro" },
        { value: "administracao" as StaffType, label: "Administração" }
      ]
    }
    
    return [
      { value: "porteiro" as StaffType, label: "Porteiro" },
      { value: "zelador" as StaffType, label: "Zelador" },
      { value: "jardineiro_piscineiro" as StaffType, label: "Jardineiro/Piscineiro" },
      { value: "auxiliar_servicos_gerais" as StaffType, label: "Auxiliar de Serviços Gerais" }
    ]
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Colaborador</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Dados Pessoais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Nome Completo *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Digite o nome completo"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  value={formData.cpf}
                  onChange={(e) => handleCpfChange(e.target.value)}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>
            </div>
          </div>

          {/* Função e Jornada */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Função</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="staff_type">Função *</Label>
                <Select
                  value={formData.staff_type}
                  onValueChange={(value: StaffType) => setFormData(prev => ({ ...prev, staff_type: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a função" />
                  </SelectTrigger>
                  <SelectContent>
                    {getStaffTypeOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(isPontoEletronico || condominium?.type === 'controle_acessos') && (
                <div className="space-y-2">
                  <Label htmlFor="workload_hours">Jornada de Trabalho (horas)</Label>
                  <Select
                    value={formData.workload_hours.toString()}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, workload_hours: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a jornada" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8">8 horas</SelectItem>
                      <SelectItem value="12">12 horas</SelectItem>
                      <SelectItem value="24">24 horas</SelectItem>
                      <SelectItem value="40">40 horas semanais</SelectItem>
                      <SelectItem value="44">44 horas semanais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Foto */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Foto</h3>
            <PhotoCapture
              onPhotoSelected={(url) => setFormData(prev => ({ ...prev, photo_url: url }))}
              currentPhoto={formData.photo_url}
            />
          </div>

          {/* Contato */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Contato</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Digite o e-mail"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Digite o telefone"
                />
              </div>
            </div>
          </div>

          {/* Acesso ao Sistema */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Acesso ao Sistema</h3>
            <div className="max-w-md space-y-3">
              <Label htmlFor="edit_password">Nova Senha</Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    id="edit_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Deixe vazio para manter a atual"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newPassword = generatePassword(10)
                    setGeneratedPassword(newPassword)
                    setFormData(prev => ({ ...prev, password: newPassword }))
                    toast({ title: "Senha gerada!", description: "Uma nova senha foi gerada automaticamente." })
                  }}
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Gerar
                </Button>
              </div>
              {generatedPassword && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">💡 Anote esta senha:</p>
                  <p className="text-sm font-mono font-semibold text-foreground">{generatedPassword}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateStaffMember.isPending || !formData.full_name || !formData.staff_type}
              className="bg-ita-blue hover:bg-ita-blue/90"
            >
              {updateStaffMember.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditarColaboradorModal
